import React, { useEffect, useMemo, useRef, useState } from "react";
import { FaLock, FaCheckCircle } from "react-icons/fa";
import "./ManagerPinModal.css";

/**
 * ManagerPinModal — uniform manager-approval surface per ADR-035 doctrine.
 *
 * Reuses the GIFTCARD-FLOW-006 manager-PIN modal pattern (6-digit numeric
 * keypad + required reason text field + Cancel/Confirm). The intent variant
 * supplies the title/subtitle/placeholder copy; the visual treatment is
 * uniform across all manager-approval surfaces (corp-family unlink,
 * gift-card undo, etc.) so staff/managers see one consistent gate.
 *
 * Security: the entered PIN value is NEVER logged anywhere client-side
 * (no console.log, no localStorage, no persistent state outside this
 * component's lifetime). The masked input clears on bad-PIN error so the
 * next attempt is unambiguous.
 *
 * Props:
 *  - isOpen: boolean — controls visibility
 *  - title: string — modal header title
 *  - subtitle: string — modal header subtitle (dynamic context)
 *  - reasonPlaceholder: string — placeholder for the reason textarea
 *  - reasonRequired: boolean — defaults true (per ADR-035 spec)
 *  - pinLength: number — defaults 6 (per ADR-035 spec)
 *  - inFlight: boolean — disables controls while parent's mutation is pending
 *  - error: string | null — error banner above PIN row (e.g. "Invalid manager PIN")
 *  - successFlash: boolean — show 1.5s green-check success state
 *  - onCancel: () => void
 *  - onConfirm: ({ managerPin, reason }) => void
 *  - onErrorCleared: () => void — parent should clear error on PIN re-entry
 */
const KEYPAD_LAYOUT = [
  ["1", "2", "3"],
  ["4", "5", "6"],
  ["7", "8", "9"],
  ["clear", "0", "back"],
];

const ManagerPinModal = ({
  isOpen,
  title,
  subtitle,
  reasonPlaceholder = "e.g. operational context",
  reasonRequired = true,
  pinLength = 6,
  inFlight = false,
  error = null,
  successFlash = false,
  onCancel,
  onConfirm,
  onErrorCleared,
}) => {
  const [pin, setPin] = useState("");
  const [reason, setReason] = useState("");
  const [shake, setShake] = useState(false);
  const pinDisplayRef = useRef(null);

  // Reset state when modal opens/closes
  useEffect(() => {
    if (isOpen) {
      setPin("");
      setReason("");
      setShake(false);
    }
  }, [isOpen]);

  // Clear PIN + shake on error
  useEffect(() => {
    if (error) {
      setPin("");
      setShake(true);
      const timer = setTimeout(() => setShake(false), 220);
      return () => clearTimeout(timer);
    }
    return undefined;
  }, [error]);

  // Keyboard support for numeric entry + Esc cancel.
  //
  // Bug fix (CORP-FAMILY-PIN-BACKSPACE-FOLLOWUP, 2026-06-12): the modal-level
  // document keydown listener was intercepting Backspace and routing it to the
  // PIN keypad even while the Reason textarea was focused — causing backspace
  // in the Reason field to nuke a PIN digit instead of deleting a reason char.
  // The fix is to detect when the active target is a text-input-like element
  // (textarea, text input, contenteditable) and skip the PIN-routing branches
  // so the browser's native edit behavior runs. Esc-cancel still fires
  // globally (matches deployed UX); 0-9 keystrokes typed inside the Reason
  // textarea are also passed through (digits in a reason are legitimate
  // content; they should NOT also fill the PIN row).
  useEffect(() => {
    if (!isOpen) return undefined;
    const handler = (event) => {
      if (event.key === "Escape" && !inFlight) {
        onCancel?.();
        return;
      }
      if (inFlight) return;
      // Skip PIN-routing for keystrokes that originated inside a text-input-
      // like element. The browser's native edit handling runs for those.
      const target = event.target;
      if (target && typeof target === "object") {
        const tag = target.tagName;
        const type = target.type;
        const isTextInputLike =
          tag === "TEXTAREA" ||
          (tag === "INPUT" &&
            (type === undefined ||
              type === "text" ||
              type === "search" ||
              type === "email" ||
              type === "password" ||
              type === "url" ||
              type === "tel" ||
              type === "number")) ||
          target.isContentEditable === true;
        if (isTextInputLike) return;
      }
      if (event.key >= "0" && event.key <= "9") {
        handleKeyPress(event.key);
      } else if (event.key === "Backspace") {
        handleKeyPress("back");
      }
    };
    document.addEventListener("keydown", handler);
    return () => document.removeEventListener("keydown", handler);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, inFlight, pin]);

  const handleKeyPress = (key) => {
    if (inFlight || successFlash) return;
    if (error && onErrorCleared) {
      onErrorCleared();
    }
    if (key === "clear") {
      setPin("");
      return;
    }
    if (key === "back") {
      setPin((current) => current.slice(0, -1));
      return;
    }
    setPin((current) => {
      if (current.length >= pinLength) return current;
      return current + key;
    });
  };

  const reasonValid = useMemo(
    () => (reasonRequired ? reason.trim().length > 0 : true),
    [reason, reasonRequired]
  );

  const canConfirm = pin.length === pinLength && reasonValid && !inFlight;

  const handleConfirm = () => {
    if (!canConfirm) return;
    onConfirm?.({ managerPin: pin, reason: reason.trim() });
  };

  const handleBackdropClick = () => {
    if (inFlight || successFlash) return;
    onCancel?.();
  };

  if (!isOpen) return null;

  return (
    <div
      className="manager-pin-modal-backdrop"
      onMouseDown={handleBackdropClick}
      role="presentation"
    >
      <div
        className={`manager-pin-modal${shake ? " is-shaking" : ""}`}
        role="dialog"
        aria-modal="true"
        aria-labelledby="manager-pin-modal-title"
        onMouseDown={(event) => event.stopPropagation()}
      >
        <div className="manager-pin-modal-header">
          <span className="manager-pin-modal-lock" aria-hidden="true">
            <FaLock />
          </span>
          <h2 id="manager-pin-modal-title" className="manager-pin-modal-title">
            {title}
          </h2>
          {subtitle ? (
            <p className="manager-pin-modal-subtitle">{subtitle}</p>
          ) : null}
        </div>

        {successFlash ? (
          <div className="manager-pin-modal-success" role="status">
            <span className="manager-pin-modal-success-icon" aria-hidden="true">
              <FaCheckCircle />
            </span>
            <span>Manager approval confirmed.</span>
          </div>
        ) : (
          <>
            {error ? (
              <div className="manager-pin-modal-error" role="alert">
                {error}
              </div>
            ) : null}

            <div
              ref={pinDisplayRef}
              className="manager-pin-display"
              aria-live="polite"
              aria-label={`Manager PIN — ${pin.length} of ${pinLength} digits entered`}
            >
              {Array.from({ length: pinLength }).map((_, index) => (
                <span
                  key={index}
                  className={`manager-pin-display-box${
                    pin.length > index ? " is-filled" : ""
                  }`}
                >
                  {pin.length > index ? "•" : ""}
                </span>
              ))}
            </div>

            <div className="manager-pin-keypad" role="group" aria-label="Manager PIN numeric keypad">
              {KEYPAD_LAYOUT.flat().map((key) => (
                <button
                  key={key}
                  type="button"
                  className={`manager-pin-keypad-button${
                    key === "clear" || key === "back" ? " is-action" : ""
                  }`}
                  onClick={() => handleKeyPress(key)}
                  disabled={inFlight}
                  aria-label={
                    key === "clear"
                      ? "Clear PIN"
                      : key === "back"
                      ? "Delete last digit"
                      : `Enter ${key}`
                  }
                >
                  {key === "clear" ? "Clear" : key === "back" ? "←" : key}
                </button>
              ))}
            </div>

            <label className="manager-pin-reason-label" htmlFor="manager-pin-reason-input">
              Reason{reasonRequired ? <span className="manager-pin-required">*</span> : null}
            </label>
            <textarea
              id="manager-pin-reason-input"
              className="manager-pin-reason-input"
              value={reason}
              onChange={(event) => {
                if (event.target.value.length <= 500) {
                  setReason(event.target.value);
                }
              }}
              placeholder={reasonPlaceholder}
              rows={4}
              maxLength={500}
              aria-required={reasonRequired}
              aria-describedby="manager-pin-reason-counter"
              disabled={inFlight}
            />
            <div className="manager-pin-reason-counter" id="manager-pin-reason-counter">
              {reason.length} / 500
            </div>
          </>
        )}

        <div className="manager-pin-modal-footer">
          <button
            type="button"
            className="manager-pin-modal-cancel"
            onClick={onCancel}
            disabled={inFlight || successFlash}
          >
            Cancel
          </button>
          <button
            type="button"
            className="manager-pin-modal-confirm"
            onClick={handleConfirm}
            disabled={!canConfirm}
            aria-disabled={!canConfirm}
          >
            {inFlight ? (
              <span className="manager-pin-modal-spinner" aria-label="Submitting" />
            ) : (
              "Confirm"
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ManagerPinModal;
